/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { message, Alert, Form, Space, Descriptions } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { v4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from 'store/session';
import { SendDataModal } from 'components/Modal/SendDataModal';
import { captureException } from '@sentry/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { InventoryMeterOutputDescriptions } from 'components/descriptions/InventoryMeterOutput';
import { useStore, selectInventorizePayloadFromForm, selectInventorizeFormPhotos } from '../../useStore';
import { useScrollTopOnMount } from '../../../../hooks/useScrollTopOnMount';
import { getInventoryMetersApi } from '../../../../api';
import { InventorizeStep, useInventorizeStepsStore } from '../../components/store';
import { LocationDescriptions } from './descriptions/Location';
import { DetailDescriptions } from './descriptions/Detail';
import { getInventoryMetersDbApi } from '../../../../api/indexed_db/inventoryMetersDb';
import { useMeterContext } from '../../../../contexts/Meter';
import { ReadingDescriptions } from './descriptions/Reading';
import { RegisterDescriptions } from './descriptions/Register';
import { ReviewStepContent as messages } from './messages';
import { useMeterModelSuggestionsStore } from '../../../../store/useMeterModelSuggestionsStore';

export const ReviewStepContent: React.VFC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { enmonMeter, inventoryMeter } = useMeterContext();
  const { apiKey, email } = useSessionStore();
  const {
    newMeter,
    lat,
    lng,
    reset,
    setQueuedMetersCount,
    setIsSendingData,
    isSendingData,
    outputs,
    internalNote,
    setDetailStepValues,
  } = useStore();
  const { reset: resetMeterModelSuggestionsStore } = useMeterModelSuggestionsStore();
  const [memoizeValues, setMemoizeValues] = useState<boolean>(false);
  const [visible, setVisible] = useState(false);

  const inventoryMeterId = inventoryMeter?.id;
  const enmonMeterId = enmonMeter?._id;

  const { setCurrentStep } = useInventorizeStepsStore();
  const data = useStore(selectInventorizePayloadFromForm(enmonMeterId, inventoryMeterId, email));
  const formPhotos = useStore(selectInventorizeFormPhotos);

  const forceScrollTop = useScrollTopOnMount();

  const handleOnSubmit = async () => {
    setIsSendingData(true);

    try {
      await getInventoryMetersApi().inventorize({ data, formPhotos });
      message.success(intl.formatMessage(messages.recordSentSuccess));
      reset(memoizeValues);
      resetMeterModelSuggestionsStore();
      setCurrentStep(InventorizeStep.LocationStep);

      if (inventoryMeterId) navigate(`/meter/${inventoryMeterId}`);
    } catch (error) {
      message.error(intl.formatMessage(messages.recordSentError));
      console.error(error);
      captureException(error);
    } finally {
      setIsSendingData(false);
      forceScrollTop();
    }
  };

  const handleSaveRecord = async () => {
    setVisible(false);
    const inventoryMetersDb = getInventoryMetersDbApi();
    await inventoryMetersDb.insertData([
      { id: v4(), locationApiKey: apiKey ?? '', meter: { data, formPhotos }, wasSynced: 0 },
    ]);
    const metersCount = await inventoryMetersDb.countNotSynchronized();
    setQueuedMetersCount(metersCount);
    message.success(intl.formatMessage(messages.queuedMetersSentSuccess));
    reset(memoizeValues);
    resetMeterModelSuggestionsStore();
    setCurrentStep(InventorizeStep.LocationStep);

    if (inventoryMeterId) navigate(`/meter/${inventoryMeterId}`);
  };

  return (
    <>
      <Form id="reviewStepForm" layout="horizontal" name="basic" onFinish={() => setVisible(true)} />
      <Space className="w-100" direction="vertical">
        {!(lat && lng) && (
          <div className="w-100">
            <Alert
              showIcon
              description={<FormattedMessage {...messages.mapPositionError} />}
              message="Error"
              type="error"
            />
          </div>
        )}

        <Descriptions bordered size="small" title={<FormattedMessage {...messages.note} />}>
          <Descriptions.Item span={2}>
            <TextArea
              autoSize={{ minRows: 3, maxRows: 5 }}
              placeholder={intl.formatMessage(messages.notePlaceholder)}
              style={{ marginTop: 8 }}
              value={internalNote ?? ''}
              onChange={e => setDetailStepValues({ internalNote: e.target.value })}
            />
          </Descriptions.Item>
        </Descriptions>

        <LocationDescriptions />

        <DetailDescriptions />

        {!newMeter && (
          <>
            <ReadingDescriptions />
            <RegisterDescriptions />
          </>
        )}

        {(outputs ?? []).map((output, index) => (
          <InventoryMeterOutputDescriptions key={index} order={index + 1} output={output} />
        ))}
      </Space>
      <SendDataModal
        isSendingData={isSendingData}
        isVisible={visible}
        memoizeValues={memoizeValues}
        onCancel={() => setVisible(false)}
        onHandleSubmit={handleOnSubmit}
        onMemoizeChange={setMemoizeValues}
        onSaveRecord={handleSaveRecord}
      />
    </>
  );
};
